import { Injectable } from '@angular/core';
import type { PhotoUpload } from './photo-upload.service';

export interface StoredPhotoUpload extends PhotoUpload {
  storageId: string;
  scope: string;
  createdAt: number;
}
interface Lease { scope: string; runner: string; expires: number; }
export class PhotoLeaseLost extends Error {}
export const photoFinished = (state: string) => state === 'saved' || state === 'cancelled';

@Injectable({ providedIn: 'root' })
export class PhotoUploadStore {
  private connection?: Promise<IDBDatabase>;

  private open(): Promise<IDBDatabase> {
    if (this.connection) return this.connection;
    this.connection = new Promise((resolve, reject) => {
      if (!globalThis.indexedDB) return reject(new Error('Device storage is unavailable.'));
      const request = indexedDB.open('nzp-photo-uploads', 1);
      let blocked = false;
      request.onupgradeneeded = () => {
        const uploads = request.result.createObjectStore('uploads', { keyPath: 'storageId' });
        uploads.createIndex('account', ['scope', 'owner']);
        request.result.createObjectStore('leases', { keyPath: 'scope' });
      };
      request.onerror = () => reject(request.error);
      request.onblocked = () => { blocked = true; reject(new Error('Close other app tabs and try device storage again.')); };
      request.onsuccess = () => {
        const db = request.result;
        if (blocked) { db.close(); return; }
        db.onversionchange = () => { db.close(); this.connection = undefined; };
        resolve(db);
      };
    });
    this.connection.catch(() => { this.connection = undefined; });
    return this.connection;
  }

  private async transaction<T>(stores: string[], mode: IDBTransactionMode,
    work: (tx: IDBTransaction, result: (value: T) => void, fail: (error: Error) => void) => void): Promise<T> {
    const db = await this.open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(stores, mode);
      let value: T;
      let failure: Error | null = null;
      tx.oncomplete = () => resolve(value);
      tx.onabort = () => reject(failure || tx.error || new Error('Device storage could not finish saving.'));
      tx.onerror = () => { /* onabort reports errors after the transaction rolls back. */ };
      const fail = (error: Error) => { failure = error; try { tx.abort(); } catch { /* Already aborted. */ } };
      try { work(tx, result => value = result, fail); } catch (error) { fail(error as Error); }
    });
  }

  load(scope: string, owner: string): Promise<StoredPhotoUpload[]> {
    return this.transaction(['uploads'], 'readonly', (tx, done) => {
      const request = tx.objectStore('uploads').index('account').getAll(IDBKeyRange.only([scope, owner]));
      request.onsuccess = () => done((request.result as StoredPhotoUpload[]).sort((a, b) => a.createdAt - b.createdAt));
    });
  }

  add(rows: StoredPhotoUpload[]): Promise<void> {
    return this.transaction(['uploads'], 'readwrite', (tx, done, fail) => {
      const store = tx.objectStore('uploads');
      const request = store.getAll();
      request.onsuccess = () => {
        try {
          const unfinished = (request.result as StoredPhotoUpload[]).filter(row => !photoFinished(row.state));
          const bytes = [...unfinished, ...rows].reduce((sum, row) => sum + ((row.prepared || row.file)?.size ?? 0), 0);
          if (unfinished.length + rows.length > 30 || bytes > 150 * 1024 * 1024) {
            fail(new Error('The device upload queue is full. Finish or remove pending photos first.'));
            return;
          }
          for (const row of rows) store.add(row);
          done();
        } catch (error) { fail(error as Error); }
      };
    });
  }

  change(item: StoredPhotoUpload, patch: Partial<PhotoUpload>, runner?: string): Promise<StoredPhotoUpload | undefined> {
    return this.transaction(['uploads', 'leases'], 'readwrite', (tx, done, fail) => {
      const update = () => {
        const store = tx.objectStore('uploads');
        const request = store.get(item.storageId);
        request.onsuccess = () => {
          try {
            const row = request.result as StoredPhotoUpload | undefined;
            if (!row || row.scope !== item.scope || row.owner !== item.owner || photoFinished(row.state)) { done(row); return; }
            const updated = { ...row, ...patch };
            store.put(updated);
            done(updated);
          } catch (error) { fail(error as Error); }
        };
      };
      if (!runner) { update(); return; }
      const request = tx.objectStore('leases').get(item.scope);
      request.onsuccess = () => {
        const lease = request.result as Lease | undefined;
        if (!lease || lease.runner !== runner || lease.expires <= Date.now()) fail(new PhotoLeaseLost('Another tab is managing uploads.'));
        else update();
      };
    });
  }

  dismiss(scope: string, owner: string): Promise<void> {
    return this.transaction(['uploads'], 'readwrite', (tx, done) => {
      const store = tx.objectStore('uploads');
      const request = store.index('account').getAll(IDBKeyRange.only([scope, owner]));
      request.onsuccess = () => {
        for (const row of request.result as StoredPhotoUpload[]) if (photoFinished(row.state)) store.delete(row.storageId);
        done();
      };
    });
  }

  lease(scope: string, runner: string, renewOnly = false, browserLockHeld = false): Promise<boolean> {
    return this.transaction(['leases'], 'readwrite', (tx, done) => {
      const store = tx.objectStore('leases');
      const request = store.get(scope);
      request.onsuccess = () => {
        const lease = request.result as Lease | undefined;
        if ((renewOnly && lease?.runner !== runner) || (!browserLockHeld && lease && lease.runner !== runner && lease.expires > Date.now())) { done(false); return; }
        store.put({ scope, runner, expires: Date.now() + 120000 });
        done(true);
      };
    });
  }

  release(scope: string, runner: string): Promise<void> {
    return this.transaction(['leases'], 'readwrite', (tx, done) => {
      const store = tx.objectStore('leases');
      const request = store.get(scope);
      request.onsuccess = () => {
        if ((request.result as Lease | undefined)?.runner === runner) store.delete(scope);
        done();
      };
    });
  }
}
